/**
 * The athlete Marketplace: opportunities brands have opened to applications, by deal type.
 * List at /opportunities/<type>, apply at /opportunity/<id>. The Profile setup screen and the
 * Deals page link here. Data comes from the opportunity endpoints in redux/collaborationsApi.
 */

export type OpportunityDealType = 'content' | 'gifting' | 'appearance' | 'licensing'

export const DEAL_TYPES = [
  {
    id: 'all',
    label: 'All',
    title: 'Opportunities',
    blurb: 'Deals you can apply to, best fit first.',
    dealType: null,
  },
  {
    id: 'gifting',
    label: 'Gifting',
    title: 'Gifting opportunities',
    blurb: 'Brands send you product to try and post about.',
    dealType: 'gifting',
  },
  {
    id: 'appearances',
    label: 'Appearances',
    title: 'Appearance opportunities',
    blurb: 'Show up in person for a brand: events, signings, clinics.',
    dealType: 'appearance',
  },
  {
    id: 'licensing',
    label: 'Licensing',
    title: 'Licensing opportunities',
    blurb: 'Make videos and photos a brand approves and licenses for its paid ads.',
    dealType: 'licensing',
  },
] as const

export type DealTypeId = (typeof DEAL_TYPES)[number]['id']

export const opportunitiesPath = (id: DealTypeId | string) => `/opportunities/${id}`
export const opportunityPath = (id: string) => `/opportunity/${id}`

/** The list tab an opportunity belongs to */
export const dealTypeTab = (dealType: OpportunityDealType): DealTypeId =>
  (DEAL_TYPES.find((d) => d.dealType === dealType)?.id ?? 'all') as DealTypeId

export type Opportunity = {
  id: string
  title: string
  dealType: OpportunityDealType
  dealTypeLabel: string
  brand: { id: string; name: string; logo: string | null } | null
  payout: number | null
  platforms: string[]
  brief: string | null
  instructions: string | null
  imageUrl: string | null
  /** Gifting: the product the brand sends */
  product: { name: string; description: string | null; imageUrl: string | null } | null
  deadline: string | null
  endDate: string | null
  audience: { sports?: number[]; schools?: number[]; states?: string[]; min_level?: string } | null
  fit: number
  fitReasons: string[]
  applied: { dealId: string; status: string } | null
  /** Gifting: the bonus ladder and paid usage included */
  gifting: { usageDays: number; bonuses: { views: number; amount: number }[] } | null
}

/** The most a gifting post can earn in bonuses */
export const maxBonus = (o: Pick<Opportunity, 'gifting'>) =>
  (o.gifting?.bonuses ?? []).reduce((s, b) => s + b.amount, 0)

/** "Product + up to $2,015 in bonuses", "$750", "$1,200 a month" */
export const opportunityPay = (o: Pick<Opportunity, 'dealType' | 'payout' | 'gifting'>) => {
  if (o.dealType === 'gifting') {
    const max = maxBonus(o)
    return max ? `Product + up to $${max.toLocaleString('en-US')} in bonuses` : 'Free product'
  }
  if (!o.payout) return 'Free product'
  const amount = `$${Math.round(o.payout).toLocaleString()}`
  return o.dealType === 'licensing' ? `${amount} a month` : amount
}

/** What the athlete will do once the brand says yes, in plain words */
export const HOW_IT_WORKS: Record<OpportunityDealType, string[]> = {
  content: ['Pitch your concept', 'Make and submit the content', 'Post it when approved'],
  gifting: ['The brand ships you the product', 'Confirm it arrived', 'Post about it'],
  appearance: ['The brand sends the details', 'Check in when you arrive', 'The brand confirms'],
  licensing: ['Create a draft', 'The brand approves it', 'Content goes live'],
}

/** "Apply by Oct 27" / "Closes today" / null */
export const deadlineLabel = (deadline: string | null) => {
  if (!deadline) return null
  const [y, m, day] = deadline.slice(0, 10).split('-').map(Number)
  if (!y || !m || !day) return null
  const d = new Date(y, m - 1, day, 23, 59)
  const days = Math.floor((d.getTime() - Date.now()) / 86400000)
  if (days < 0) return 'Closed'
  if (days === 0) return 'Closes today'
  if (days <= 3) return `${days} day${days === 1 ? '' : 's'} left`
  return `Apply by ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
}
